import type { Alarm, AlarmStatus } from "@fts/core";
import { useI18n } from "../i18n";
import { formatClock } from "../lib/alarms";

/**
 * "Mesai dışı", "Gecikmeli" / "Zamanı belirsiz" and "×N tekrar, son: HH:MM" badges; the hint is in the
 * title and in screen-reader text.
 */
export function AlarmBadges({ alarm }: { alarm: Pick<Alarm, "offHours" | "delayed"> & Partial<Pick<Alarm, "timeUnknown" | "repeatCount" | "lastOccurredAt">> }) {
  const { t, locale } = useI18n();
  const repeats = alarm.repeatCount ?? 0;
  if (!alarm.offHours && !alarm.delayed && !alarm.timeUnknown && repeats === 0) return null;
  const last = alarm.lastOccurredAt ? formatClock(new Date(alarm.lastOccurredAt), locale) : null;
  return (
    <span className="alarm-badges">
      {alarm.offHours && (
        <span className="badge tone-info" title={t("alarm.badge.offHoursHint")}>
          {t("alarm.badge.offHours")}
        </span>
      )}
      {alarm.timeUnknown ? (
        <span className="badge tone-warn" title={t("alarm.badge.timeUnknownHint")}>
          {t("alarm.badge.timeUnknown")}
        </span>
      ) : (
        alarm.delayed && (
          <span className="badge tone-neutral" title={t("alarm.badge.delayedHint")}>
            {t("alarm.badge.delayed")}
          </span>
        )
      )}
      {repeats > 0 && (
        <span className="badge tone-neutral" title={t("alarm.badge.repeatHint")}>
          {last ? t("alarm.badge.repeat", { count: repeats, time: last }) : t("alarm.badge.repeatNoTime", { count: repeats })}
        </span>
      )}
    </span>
  );
}

export function AlarmStatusBadge({ status }: { status: AlarmStatus }) {
  const { t } = useI18n();
  return <span className={`badge ${status === "open" ? "tone-crit" : "tone-ok"}`}>{t(`alarm.status.${status}`)}</span>;
}
